import { useEffect, useState } from "react";
import { adminRetentionOffers, adminRetentionDecline, adminRetentionRevert, adminRetentionFire } from "@/lib/api";

const NAVY = "#0A1628";
const GOLD = "#D4AF37";
const BG = "#F8F9FA";

const STATUS_TABS = ["offered", "accepted", "expired", "declined", "reverted"];
const TAB_LABEL = {
  offered: "Pending",
  accepted: "Active Saves",
  expired: "Expired",
  declined: "Declined",
  reverted: "Reverted",
};

const fmtCents = (c) => `$${((c || 0) / 100).toFixed(0)}`;
const fmtDate = (s) => (s ? new Date(s).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "—");

const statusBadge = (status) => {
  const map = {
    offered: { bg: GOLD, fg: NAVY },
    accepted: { bg: "#1A7A4A", fg: "#fff" },
    expired: { bg: "#9ca3af", fg: "#fff" },
    declined: { bg: "#A32D2D", fg: "#fff" },
    reverted: { bg: NAVY, fg: GOLD },
  };
  const s = map[status] || { bg: "#e5e7ee", fg: NAVY };
  return (
    <span style={{ background: s.bg, color: s.fg, padding: "4px 10px", borderRadius: 999, fontSize: 11, letterSpacing: 1.5, fontWeight: 700 }}>
      {status.toUpperCase()}
    </span>
  );
};

export default function AdminRetentionPage() {
  const [tab, setTab] = useState("offered");
  const [data, setData] = useState({ offers: [], counts: {}, monthly_mrr_saved_dollars: 0 });
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [fireId, setFireId] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const result = await adminRetentionOffers(tab);
      setData(result);
    } catch (e) {
      console.error("retention list failed", e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [tab]);

  const handleDecline = async (offerId) => {
    const reason = window.prompt("Reason for declining this offer? (optional)") ?? "";
    setBusy(offerId);
    try {
      await adminRetentionDecline(offerId, reason);
      await load();
    } catch (e) {
      alert("Decline failed: " + (e?.response?.data?.detail || "error"));
    } finally {
      setBusy(null);
    }
  };

  const handleRevert = async () => {
    if (!window.confirm("Run the revert sweep now? Saves whose 90 days have passed will be bumped back to their original plan in Stripe.")) return;
    setBusy("revert");
    try {
      const result = await adminRetentionRevert();
      alert(`Revert sweep complete. Reverted=${result.reverted}, skipped=${result.skipped}, errors=${result.errors}.`);
      await load();
    } catch (e) {
      alert("Revert failed: " + (e?.response?.data?.detail || "error"));
    } finally {
      setBusy(null);
    }
  };

  const handleFire = async () => {
    if (!fireId) return;
    setBusy("fire");
    try {
      const result = await adminRetentionFire(fireId.trim(), "admin_manual");
      if (result.ok) {
        alert(`Offer fired. Email sent=${result.email_sent}, SMS sent=${result.sms_sent}. Token: ${result.token}`);
        setFireId("");
      } else {
        alert(`Could not fire: ${result.error}`);
      }
      await load();
    } catch (e) {
      alert("Fire failed: " + (e?.response?.data?.detail || "error"));
    } finally {
      setBusy(null);
    }
  };

  return (
    <div style={{ background: BG, minHeight: "100vh", padding: "32px 24px" }} data-testid="admin-retention-page">
      <div style={{ maxWidth: 1200, margin: "0 auto" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 16 }}>
          <div>
            <div style={{ color: GOLD, letterSpacing: 3, fontSize: 12, fontWeight: 700 }}>RETENTION · ANNIVERSARY SAVE</div>
            <h1 style={{ fontFamily: "Bebas Neue, Arial, sans-serif", letterSpacing: 2, fontSize: 44, color: NAVY, margin: "4px 0 0" }}>
              SAVE THE CHURNERS · $49/MO FOR 90 DAYS
            </h1>
          </div>
          <button onClick={handleRevert} disabled={busy === "revert"} data-testid="retention-revert-btn"
                  style={btnSecondary}>
            {busy === "revert" ? "Reverting…" : "RUN REVERT SWEEP"}
          </button>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 12, marginTop: 28 }}>
          <KpiTile label="MRR Saved" value={`$${data.monthly_mrr_saved_dollars}`} sub="/month while active" />
          <KpiTile label="Pending Offers" value={data.counts.offered || 0} />
          <KpiTile label="Active Saves" value={data.counts.accepted || 0} />
          <KpiTile label="Expired" value={data.counts.expired || 0} />
          <KpiTile label="Reverted" value={data.counts.reverted || 0} />
        </div>

        <div style={{ background: "#fff", border: "1px solid #e5e7ee", borderRadius: 14, padding: 18, marginTop: 24 }}>
          <div style={{ color: NAVY, fontWeight: 700, marginBottom: 10, letterSpacing: 1 }}>FIRE AN OFFER MANUALLY</div>
          <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
            <input type="text" placeholder="contractor_id"
                   value={fireId} onChange={(e) => setFireId(e.target.value)}
                   data-testid="retention-fire-contractor-id"
                   style={{ flex: "1 1 300px", padding: "12px 14px", border: "1px solid #d0d3da", borderRadius: 10, fontSize: 15 }} />
            <button onClick={handleFire} disabled={!fireId || busy === "fire"}
                    data-testid="retention-fire-btn"
                    style={{ ...btnPrimary, opacity: !fireId ? 0.5 : 1 }}>
              {busy === "fire" ? "Firing…" : "FIRE OFFER"}
            </button>
          </div>
        </div>

        <div style={{ display: "flex", gap: 6, marginTop: 28, flexWrap: "wrap" }}>
          {STATUS_TABS.map((t) => (
            <button key={t} onClick={() => setTab(t)}
                    data-testid={`retention-tab-${t}`}
                    style={{ ...tabBtn, ...(tab === t ? tabBtnActive : {}) }}>
              {TAB_LABEL[t]} ({data.counts[t] || 0})
            </button>
          ))}
        </div>

        <div style={{ background: "#fff", borderRadius: 14, border: "1px solid #e5e7ee", marginTop: 12, overflow: "hidden" }}>
          {loading ? (
            <div style={{ padding: 32, color: "#5b5e69" }}>Loading…</div>
          ) : data.offers.length === 0 ? (
            <div data-testid="retention-empty" style={{ padding: 32, color: "#5b5e69" }}>No offers in this state.</div>
          ) : (
            <table style={{ width: "100%", borderCollapse: "collapse" }} data-testid="retention-table">
              <thead style={{ background: BG }}>
                <tr>
                  <th style={th}>Contractor</th>
                  <th style={th}>Plan → Save</th>
                  <th style={th}>Trigger</th>
                  <th style={th}>Status</th>
                  <th style={th}>Created</th>
                  <th style={th}>Expires / Reverts</th>
                  <th style={th}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {data.offers.map((o) => (
                  <tr key={o.offer_id} style={{ borderTop: "1px solid #e5e7ee" }}>
                    <td style={td}>
                      <div style={{ fontWeight: 700, color: NAVY }}>{o.business_name || "—"}</div>
                      <div style={{ fontSize: 12, color: "#5b5e69" }}>{o.owner_name}</div>
                      <div style={{ fontSize: 11, color: "#9ca3af", fontFamily: "monospace" }}>{o.contractor_id}</div>
                    </td>
                    <td style={td}>
                      <strong>{(o.current_plan || "").toUpperCase()}</strong>
                      <span style={{ color: GOLD, padding: "0 6px" }}>→</span>
                      $49<span style={{ color: "#5b5e69" }}>/mo</span>
                    </td>
                    <td style={td}><code style={codeStyle}>{o.trigger_reason}</code></td>
                    <td style={td}>{statusBadge(o.status)}</td>
                    <td style={td}>{fmtDate(o.created_at)}</td>
                    <td style={td}>
                      {o.status === "offered" && fmtDate(o.expires_at)}
                      {o.status === "accepted" && (
                        <>
                          <div style={{ color: "#1A7A4A", fontWeight: 700 }}>Reverts {fmtDate(o.revert_at)}</div>
                        </>
                      )}
                      {o.status === "reverted" && fmtDate(o.reverted_at)}
                      {o.status === "expired" && fmtDate(o.expires_at)}
                    </td>
                    <td style={td}>
                      <a href={o.accept_url} target="_blank" rel="noreferrer" style={linkStyle}>OFFER PAGE</a>
                      {o.status === "offered" && (
                        <button onClick={() => handleDecline(o.offer_id)}
                                disabled={busy === o.offer_id}
                                data-testid={`retention-decline-${o.offer_id}`}
                                style={{ ...btnGhost, marginLeft: 8 }}>
                          {busy === o.offer_id ? "…" : "DECLINE"}
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}

function KpiTile({ label, value, sub }) {
  return (
    <div style={{ background: "#fff", border: "1px solid #e5e7ee", borderRadius: 14, padding: 18 }}>
      <div style={{ color: "#5b5e69", letterSpacing: 2, fontSize: 11, fontWeight: 700 }}>{label.toUpperCase()}</div>
      <div style={{ color: NAVY, fontFamily: "Bebas Neue, Arial, sans-serif", fontSize: 38, letterSpacing: 1, marginTop: 4 }}>{value}</div>
      {sub && <div style={{ color: "#5b5e69", fontSize: 12 }}>{sub}</div>}
    </div>
  );
}

const th = { textAlign: "left", padding: "14px 16px", color: NAVY, fontSize: 12, letterSpacing: 1.5, fontWeight: 700 };
const td = { padding: "14px 16px", color: NAVY, fontSize: 14, verticalAlign: "top" };
const tabBtn = { background: "#fff", border: "1px solid #e5e7ee", padding: "10px 16px", borderRadius: 10, cursor: "pointer", color: NAVY, fontSize: 13, fontWeight: 600 };
const tabBtnActive = { background: NAVY, color: GOLD, border: `1px solid ${NAVY}` };
const btnPrimary = { background: GOLD, color: NAVY, border: "none", padding: "12px 20px", borderRadius: 10, fontWeight: 700, letterSpacing: 1.5, cursor: "pointer" };
const btnSecondary = { background: "#fff", color: NAVY, border: `1px solid ${NAVY}`, padding: "12px 20px", borderRadius: 10, fontWeight: 700, letterSpacing: 1.5, cursor: "pointer" };
const btnGhost = { background: "transparent", color: "#A32D2D", border: "1px solid #A32D2D", padding: "6px 12px", borderRadius: 8, cursor: "pointer", fontSize: 12, fontWeight: 700 };
const linkStyle = { color: NAVY, textDecoration: "underline", fontSize: 12, fontWeight: 700 };
const codeStyle = { background: BG, padding: "2px 8px", borderRadius: 6, fontFamily: "monospace", fontSize: 12, color: NAVY };
